import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { useCookieConsent } from '@/hooks/use-cookie-consent';

import { UNCONFIRMED_CLIENT_ROLE } from '@/features/auth/hooks/use-is-unconfirmed-client';
import { useWhoami } from '@/features/auth/hooks/use-whoami-query';
import { useLegalDocuments } from '@/features/user/hooks/use-legal-documents';
import { useLogConsent } from '@/features/user/hooks/use-log-consent';

import { Typography } from '@/components/typography/typography';

import { MainButton } from '@/lib/shadcn/components/ui/main-button/main-button';
import { Stack } from '@/lib/shadcn/components/ui/stack/stack';

export function CookieBanner() {
  const { t } = useTranslation();
  const { getDocUrl } = useLegalDocuments();
  const { hasConsent, acceptAll, rejectAll } = useCookieConsent();
  const { data: whoamiData } = useWhoami({ params: {} });
  const isAuthorized =
    whoamiData !== undefined &&
    whoamiData.auth.userId !== null &&
    !whoamiData.auth.roles.includes(UNCONFIRMED_CLIENT_ROLE);
  const { mutateAsync: logConsentAsync } = useLogConsent();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    // Показываем баннер через 1 секунду после входа, если выбор еще не сделан
    if (hasConsent) {
      setIsOpen(false);
    } else {
      const timer = setTimeout(() => {
        setIsOpen(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [hasConsent]);

  const handleAccept = async () => {
    acceptAll();
    setIsOpen(false);
    if (isAuthorized) {
      try {
        await logConsentAsync({
          action: 'grant',
          formType: 'cookie',
          signedDocuments: ['metrics_consent', 'recommendation_rules'],
          checkboxesState: {
            metrics_consent: true,
            recommendation_rules: true,
          },
        });
      } catch (error) {
        console.error('Failed to log cookie consent', error);
      }
    }
  };

  const handleReject = async () => {
    setIsOpen(false);
    if (isAuthorized) {
      try {
        await logConsentAsync({
          action: 'revoke',
          formType: 'cookie',
          signedDocuments: ['metrics_consent', 'recommendation_rules'],
          checkboxesState: {
            metrics_consent: false,
            recommendation_rules: false,
          },
        });
      } catch (error) {
        console.error('Failed to log cookie rejection', error);
      }
    }
    rejectAll();
  };

  // Не выводим баннер во фрейме или если согласие уже есть
  if (globalThis.self !== window.top || hasConsent) {
    return null;
  }

  if (!isOpen) {
    return null;
  }

  return (
    <div className="pointer-events-auto fixed inset-0 z-[9999] flex">
      {/* Полноэкранный блокирующий оверлей */}
      <div
        className="fixed inset-0 bg-black-50 transition-opacity duration-200"
        aria-hidden="true"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      />

      {/* Баннер в правом нижнем углу: позиция и размеры сохранены точь-в-точь */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-banner-title"
        className="fixed bottom-4 right-4 z-[10000] flex w-[calc(100%-2rem)] max-w-sm flex-col gap-unit-4 rounded-large border border-slate-200 bg-white p-unit-6 shadow-2xl"
      >
        <Stack gap={2}>
          <Typography.h5
            id="cookie-banner-title"
            className="font-semibold text-slate-500"
          >
            {t('cookie-dialog.title')}
          </Typography.h5>
          <div>
            <Stack gap={2}>
              <Typography.small1 className="text-small1 leading-relaxed text-slate-400">
                {t('cookie-dialog.technical-text')}
              </Typography.small1>
              <Typography.small1 className="whitespace-pre-line text-small1 leading-relaxed text-slate-400">
                <Trans
                  i18nKey="cookie-dialog.text"
                  components={{
                    policy: (
                      <a
                        href={getDocUrl('privacy_policy')}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-teal-400 underline"
                      />
                    ),
                    terms: (
                      <a
                        href={getDocUrl('metrics_consent')}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-teal-400 underline"
                      />
                    ),
                    recommendations: (
                      <a
                        href={getDocUrl('recommendation_rules')}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-teal-400 underline"
                      />
                    ),
                  }}
                />
              </Typography.small1>
            </Stack>
          </div>
        </Stack>

        <Stack direction="row" gap={3} className="w-full pt-unit-1">
          <MainButton
            size="s"
            variant="outlined"
            onClick={handleReject}
            className="flex-1"
            data-testid="cookie-reject-button"
          >
            {t('cookie-dialog.reject-all')}
          </MainButton>

          <MainButton
            size="s"
            variant="filled"
            onClick={handleAccept}
            className="flex-1"
            data-testid="cookie-accept-button"
          >
            {t('cookie-dialog.accept-all')}
          </MainButton>
        </Stack>
      </div>
    </div>
  );
}
